import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle2, HelpCircle, User, Heart, Target, DollarSign, Calendar, Users, Activity, Briefcase, Sparkles, Shield, CheckSquare, Link as LinkIcon, Download } from 'lucide-react';
import HelpModal from './HelpModal';
import { getScoreColor, getScoreBgColor, formatPercentage, formatCurrency } from '../../utils/enhancedDashboardUtils';
import { PERSONAL_CATEGORY_KEY_MAP, PERSONAL_CATEGORY_DESCRIPTIONS } from '../../utils/categoryDescriptions';
import { useBusinessContext } from '../../contexts/BusinessContext';

const PersonalEnvisioning = ({ data, personalAssessment, wealthGap, profileData, onSave, saving }) => {
  const { currentBusinessId } = useBusinessContext();
  const [formData, setFormData] = useState({
    // Section A: Current Personal Snapshot
    current_age: '',
    life_stage: '',
    family_situation: '',
    key_roles: '',

    // Section B: Identity, Calling & Legacy
    identity_statement: '',
    thankful_for: '',
    proud_of: '',
    known_for: '',
    calling_purpose: '',
    guiding_scripture: '',
    legacy_vision: '',

    // Section C: S.T.E.P. Reflection Model
    step_spiritual_believe: '',
    step_spiritual_love: '',
    step_spiritual_center: '',
    step_things_have: '',
    step_things_want: '',
    step_things_dont_need: '',
    step_experiences_more_time: '',
    step_experiences_avoid: '',
    step_people_important: '',
    step_people_avoid: '',
    step_core_values_admire: '',
    step_core_values_traits: '',

    // Section D: Personal Wealth Gap
    financial_independence_number: '',
    current_net_worth: '',
    personal_wealth_gap: '',
    wealth_sources: '',
    gap_closing_strategies: '',
    investment_risk_tolerance: '',
    estate_planning_documents: [],
    primary_wealth_strategy: '',
    financial_planning_status: '',
    major_financial_concerns: '',
    source_of_wealth: [],
    source_of_wealth_other: '',
    use_of_wealth: [],
    use_of_wealth_other: '',

    // Section E: 10-Year Life Vision
    living_location: '',
    time_spending: '',
    faith_vision: '',
    work_vision: '',
    marriage_vision: '',
    giving_vision: '',
    health_vision: '',

    // Section G: 3-5 Year Personal Picture
    marriage_transformation: '',
    kids_transformation: '',
    health_transformation: '',
    spiritual_transformation: '',
    work_transformation: '',
    net_worth_transformation: '',
    changes_needed: '',

    // Section H: Perfect Week Exercise
    perfect_week_mornings: '',
    perfect_week_afternoons: '',
    perfect_week_evenings: '',
    perfect_week_weekends: '',
    current_week_reality: '',
    gap_analysis: '',

    // Section I: Relationships & Family Alignment
    relationship_priorities: '',
    key_conversations: '',
    spouse_alignment: '',
    family_concerns: '',

    // Section J: Health, Energy & Capacity
    // current_health_rating removed - now auto-populated from Physical Health & Vitality category score
    health_constraints: '',
    three_year_health_vision: '',
    health_action_steps: '',
    current_hobbies: '',
    hobbies_to_develop: '',
    time_allocation_hobbies: '',
    hobby_communities: '',

    // Section K: Work, Purpose & Contribution Post-Exit
    work_intentions: '',
    contribution_focus: '',
    avoiding_retire_die: '',
    meaningful_work_vision: '',

    // Section L: Faith, Generosity & Eternal Impact
    spiritual_growth_goals: '',
    spiritual_practices: '',
    giving_plan: '',
    eternal_impact_vision: '',

    // Section M: Five Ds (Personal)
    five_ds_personal_death_items: [],
    five_ds_personal_death: '',
    five_ds_personal_disability_items: [],
    five_ds_personal_disability: '',
    five_ds_personal_divorce_items: [],
    five_ds_personal_divorce: '',
    five_ds_personal_distress_items: [],
    five_ds_personal_distress: '',
    five_ds_personal_disagreement_items: [],
    five_ds_personal_disagreement: '',
    five_ds_personal_priorities: '',

    // Section N: 90-Day Personal Focus
    personal_sprint_priorities: ['', '', ''],
    conversations_to_have: '',
    personal_quick_wins: ['', '', ''],

    // Section O: Integration with Business Plan
    alignment_check: '',
    conflicts_to_resolve: ''
  });

  const [saveStatus, setSaveStatus] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [helpModal, setHelpModal] = useState({ isOpen: false, title: '', content: null });

  // Load data when it changes
  useEffect(() => {
    if (data && data.exists !== false) {
      setFormData(prev => ({
        ...prev,
        ...Object.keys(data).reduce((acc, key) => {
          if (key !== 'id' && key !== 'user_id' && key !== 'created_at' && key !== 'updated_at') {
            acc[key] = data[key];
          }
          return acc;
        }, {})
      }));
    }
  }, [data]);

  // Auto-populate age from profile data (calculate from date of birth)
  useEffect(() => {
    if (profileData?.client_date_of_birth && !formData.current_age) {
      const birthDate = new Date(profileData.client_date_of_birth);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      // Adjust if birthday hasn't occurred this year
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      setFormData(prev => ({
        ...prev,
        current_age: age
      }));
    }
  }, [profileData, formData.current_age]);

  // Get top strengths and bottom concerns from personal assessment
  const getTopStrengthsAndConcerns = () => {
    // Only show strengths/concerns if assessment has been completed
    if (!personalAssessment?.category_scores || !personalAssessment?.answered_questions || personalAssessment.answered_questions === 0) {
      return { strengths: [], concerns: [] };
    }

    const categories = Object.entries(personalAssessment.category_scores || {})
      .map(([key, score]) => {
        const displayName = PERSONAL_CATEGORY_KEY_MAP[key];
        const description = PERSONAL_CATEGORY_DESCRIPTIONS[displayName];
        return {
          key,
          score,
          name: displayName,
          summary: description?.summary || ''
        };
      })
      .filter(cat => cat.score !== null && cat.score !== undefined);

    // Sort by score descending
    const sorted = [...categories].sort((a, b) => b.score - a.score);

    // Get top 3 (highest scores) and bottom 3 (lowest scores)
    const strengths = sorted.slice(0, 3);
    const concerns = sorted.slice(-3).reverse();

    return { strengths, concerns };
  };

  const { strengths: autoStrengths, concerns: autoConcerns } = getTopStrengthsAndConcerns();

  // Debug personal assessment data
  useEffect(() => {
  }, [personalAssessment, profileData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const handleCheckboxChange = (field, value) => {
    setFormData(prev => {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [field]: [...current, value] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onSave(formData);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/pdf/personal-envisioning/${currentBusinessId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      const blob = await response.blob();

      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'Personal_Envisioning_Report.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const showHelp = (title, content) => {
    setHelpModal({ isOpen: true, title, content });
  };

  const closeHelp = () => {
    setHelpModal({ isOpen: false, title: '', content: null });
  };

  // Section Header Component
  const SectionHeader = ({ icon: Icon, letter, title, description, helpContent }) => (
    <div className="flex items-start justify-between mb-6">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 mt-1">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Icon className="w-5 h-5 text-purple-600" />
          </div>
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            Section {letter}: {title}
          </h2>
          {description && (
            <p className="mt-1 text-sm text-gray-600">{description}</p>
          )}
        </div>
      </div>
      {helpContent && (
        <button
          type="button"
          onClick={() => showHelp(`Section ${letter}: ${title}`, helpContent)}
          className="flex-shrink-0 p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
          title="Help"
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      )}
    </div>
  );

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-8 pb-8">
        {/* Quick Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sticky top-4 z-10">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-700">Quick Navigation</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            <a href="#section-a" className="text-xs px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition text-center font-medium">
              A: Snapshot
            </a>
            <a href="#section-b" className="text-xs px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition text-center font-medium">
              B: Identity
            </a>
            <a href="#section-c" className="text-xs px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition text-center font-medium">
              C: S.T.E.P.
            </a>
            <a href="#section-d" className="text-xs px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition text-center font-medium">
              D: Wealth Gap
            </a>
            <a href="#section-e" className="text-xs px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition text-center font-medium">
              E: Financial
            </a>
            <a href="#section-f" className="text-xs px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition text-center font-medium">
              F: 10-Year Vision
            </a>
            <a href="#section-g" className="text-xs px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition text-center font-medium">
              G: 3-5 Year Picture
            </a>
            <a href="#section-h" className="text-xs px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition text-center font-medium">
              H: Perfect Week
            </a>
            <a href="#section-i" className="text-xs px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition text-center font-medium">
              I: Relationships
            </a>
            <a href="#section-j" className="text-xs px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition text-center font-medium">
              J: Health & Hobbies
            </a>
            <a href="#section-k" className="text-xs px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition text-center font-medium">
              K: Post-Exit
            </a>
            <a href="#section-l" className="text-xs px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition text-center font-medium">
              L: Faith
            </a>
            <a href="#section-m" className="text-xs px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition text-center font-medium">
              M: Five Ds
            </a>
            <a href="#section-n" className="text-xs px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition text-center font-medium">
              N: 90-Day Focus
            </a>
            <a href="#section-o" className="text-xs px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition text-center font-medium">
              O: Integration
            </a>
          </div>
        </div>

        {/* Section A: Current Personal Snapshot */}
        <div id="section-a" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-40">
          <SectionHeader
            icon={User}
            letter="A"
            title="Current Personal Snapshot"
            description="Where are you today in life?"
            helpContent={
              <div className="space-y-3">
                <p>This section captures your current personal reality as a baseline for envisioning your future.</p>
                <p><strong>Gap Assessment Scores:</strong> Automatically populated from your Personal Gap Assessment showing Overall, Financial, Wellbeing, and Spiritual readiness scores. These scores reflect your current state across all 26 personal assessment categories.</p>
                <p><strong>Current Age:</strong> Automatically calculated from your date of birth in the Client Profile page. This provides context for your life stage and timeline planning.</p>
                <p><strong>Life Stage:</strong> Select the phase that best describes your current season: Building Years (20s-30s), Accumulation (40s-50s), Pre-Exit (Planning), or In Transition. This helps frame your vision timeline.</p>
                <p><strong>Top 3 Personal Strengths:</strong> Automatically identified as your highest-scoring category areas from the assessment. These represent areas where you're well-prepared and can build upon.</p>
                <p><strong>Top 3 Personal Concerns/Gaps:</strong> Automatically identified as your lowest-scoring category areas from the assessment. These are critical areas requiring attention and planning before your business exit.</p>
                <p><strong>Key Roles:</strong> List your primary identities and responsibilities (business owner, spouse, parent, board member, volunteer, etc.). Understanding your roles helps clarify what will change post-exit and what gives you purpose beyond business.</p>
              </div>
            }
          />

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Current Age
                  {profileData?.client_date_of_birth && (
                    <span className="ml-2 text-xs font-normal text-green-600">(Calculated from date of birth)</span>
                  )}
                </label>
                <input
                  type="number"
                  value={formData.current_age}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed"
                  readOnly
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Family Situation
                </label>
                <input
                  type="text"
                  value={formData.family_situation}
                  onChange={(e) => handleInputChange('family_situation', e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                  placeholder="e.g., Married, 3 kids"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Life Stage
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { value: 'building', label: 'Building Years (20s-30s)' },
                  { value: 'accumulation', label: 'Accumulation (40s-50s)' },
                  { value: 'pre_exit', label: 'Pre-Exit (Planning)' },
                  { value: 'transition', label: 'In Transition' }
                ].map((stage) => (
                  <label
                    key={stage.value}
                    className={`
                      flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all
                      ${formData.life_stage === stage.value
                        ? 'border-purple-600 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300 bg-white'
                      }
                    `}
                  >
                    <input
                      type="radio"
                      name="life_stage"
                      value={stage.value}
                      checked={formData.life_stage === stage.value}
                      onChange={(e) => handleInputChange('life_stage', e.target.value)}
                      className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-900">{stage.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Key Roles & Responsibilities
              </label>
              <textarea
                value={formData.key_roles}
                onChange={(e) => handleInputChange('key_roles', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="Business owner, spouse, parent, board member, volunteer, etc."
              />
            </div>

            {/* Personal Gap Assessment Scores - Auto-populated */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">Gap Assessment Scores</h3>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Auto-populated from Personal Gap Assessment</span>
              </div>

              {personalAssessment && personalAssessment.answered_questions > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {/* Overall Score */}
                  <div className={`text-center p-4 rounded-lg border ${getScoreBgColor(personalAssessment.overall_score || 0)}`}>
                    <p className={`text-2xl font-bold ${getScoreColor(personalAssessment.overall_score || 0)}`}>
                      {formatPercentage(personalAssessment.overall_score || 0)}
                    </p>
                    <p className="text-xs text-gray-600 mt-1 font-semibold">Overall</p>
                  </div>

                  {/* Financial Score */}
                  <div className={`text-center p-4 rounded-lg border ${getScoreBgColor(personalAssessment.financial_score || 0)}`}>
                    <p className={`text-2xl font-bold ${getScoreColor(personalAssessment.financial_score || 0)}`}>
                      {formatPercentage(personalAssessment.financial_score || 0)}
                    </p>
                    <p className="text-xs text-gray-600 mt-1 font-semibold">Financial</p>
                  </div>

                  {/* Wellbeing Score */}
                  <div className={`text-center p-4 rounded-lg border ${getScoreBgColor(personalAssessment.wellbeing_score || 0)}`}>
                    <p className={`text-2xl font-bold ${getScoreColor(personalAssessment.wellbeing_score || 0)}`}>
                      {formatPercentage(personalAssessment.wellbeing_score || 0)}
                    </p>
                    <p className="text-xs text-gray-600 mt-1 font-semibold">Wellbeing</p>
                  </div>

                  {/* Spiritual Score */}
                  <div className={`text-center p-4 rounded-lg border ${getScoreBgColor(personalAssessment.spiritual_score || 0)}`}>
                    <p className={`text-2xl font-bold ${getScoreColor(personalAssessment.spiritual_score || 0)}`}>
                      {formatPercentage(personalAssessment.spiritual_score || 0)}
                    </p>
                    <p className="text-xs text-gray-600 mt-1 font-semibold">Spiritual</p>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-6 text-center border border-gray-200">
                  <p className="text-gray-600 text-sm">
                    No assessment data available. Please complete the <strong>Personal Gap Assessment</strong> to see your scores here.
                  </p>
                </div>
              )}
            </div>

            {/* Top Strengths - Auto-populated */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-semibold text-gray-700">
                  Top 3 Personal Strengths
                </label>
                <span className="text-xs text-gray-500 bg-green-50 px-2 py-1 rounded border border-green-200">Auto-populated</span>
              </div>
              {autoStrengths.length > 0 ? (
                <div className="space-y-3">
                  {autoStrengths.map((strength, index) => (
                    <div
                      key={index}
                      className={`px-4 py-3 rounded-lg border ${getScoreBgColor(strength.score)} border-gray-200`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold mr-3">
                            {index + 1}
                          </div>
                          <div className="font-semibold text-gray-900 text-sm">{strength.name}</div>
                        </div>
                        <div className={`text-lg font-bold ml-3 ${getScoreColor(strength.score)}`}>
                          {formatPercentage(strength.score)}
                        </div>
                      </div>
                      <div className="ml-9 text-xs text-gray-600">{strength.summary}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 italic">Complete assessment to see top strengths</div>
              )}
            </div>

            {/* Top Concerns/Gaps - Auto-populated */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-semibold text-gray-700">
                  Top 3 Personal Concerns/Gaps
                </label>
                <span className="text-xs text-gray-500 bg-red-50 px-2 py-1 rounded border border-red-200">Auto-populated</span>
              </div>
              {autoConcerns.length > 0 ? (
                <div className="space-y-3">
                  {autoConcerns.map((concern, index) => (
                    <div
                      key={index}
                      className={`px-4 py-3 rounded-lg border ${getScoreBgColor(concern.score)} border-gray-200`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-bold mr-3">
                            {index + 1}
                          </div>
                          <div className="font-semibold text-gray-900 text-sm">{concern.name}</div>
                        </div>
                        <div className={`text-lg font-bold ml-3 ${getScoreColor(concern.score)}`}>
                          {formatPercentage(concern.score)}
                        </div>
                      </div>
                      <div className="ml-9 text-xs text-gray-600">{concern.summary}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 italic">Complete assessment to see top concerns</div>
              )}
            </div>
          </div>
        </div>

        {/* Section B: Identity, Calling & Legacy */}
        <div id="section-b" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-40">
          <SectionHeader
            icon={Heart}
            letter="B"
            title="Identity, Calling & Legacy"
            description="Who are you and what are you here to do?"
            helpContent={
              <div className="space-y-3">
                <p>This section helps you clarify your core identity beyond your business role and reflect on your life's purpose.</p>
                <p><strong>Identity Statement:</strong> Who are you at your core? Define yourself beyond your role as business owner or professional titles. Consider your character, values, and fundamental nature.</p>
                <p><strong>What are you most thankful for?</strong> Reflecting on gratitude helps ground you in appreciation and perspective. Identify the people, experiences, and blessings that have shaped your life.</p>
                <p><strong>What are you most proud of?</strong> Consider your accomplishments and the aspects of your life that bring you the most pride. This isn't about boasting, but recognizing what you've built and achieved that aligns with your values.</p>
                <p><strong>What Do You Want to Be Known For?</strong> Your reputation and how others perceive you. Think about family, friends, community - what legacy of character and impact do you want to leave?</p>
                <p><strong>Your Calling & Purpose:</strong> What is God calling you to do with your life and resources? This is your unique contribution and mission beyond business success.</p>
                <p><strong>Guiding Scripture or Principle:</strong> The biblical truth or principle that anchors your decisions and direction.</p>
                <p><strong>Legacy Vision:</strong> What do you want to be remembered for? What lasting impact do you want to leave on your family, community, and the kingdom?</p>
              </div>
            }
          />

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Identity Statement
              </label>
              <textarea
                value={formData.identity_statement}
                onChange={(e) => handleInputChange('identity_statement', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="Who are you at your core? (Beyond your role as business owner)"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                What are you most thankful for?
              </label>
              <textarea
                value={formData.thankful_for}
                onChange={(e) => handleInputChange('thankful_for', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="What are you most grateful for in your life?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                What are you most proud of?
              </label>
              <textarea
                value={formData.proud_of}
                onChange={(e) => handleInputChange('proud_of', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="What accomplishments or aspects of your life bring you the most pride?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                What Do You Want to Be Known For?
              </label>
              <textarea
                value={formData.known_for}
                onChange={(e) => handleInputChange('known_for', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="By family, friends, community - what do you want your reputation to be?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Your Calling & Purpose
              </label>
              <textarea
                value={formData.calling_purpose}
                onChange={(e) => handleInputChange('calling_purpose', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="What is God calling you to do with your life and resources?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Guiding Scripture or Principle
              </label>
              <input
                type="text"
                value={formData.guiding_scripture}
                onChange={(e) => handleInputChange('guiding_scripture', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="e.g., Proverbs 3:5-6, Luke 12:48, etc."
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Legacy Vision
              </label>
              <textarea
                value={formData.legacy_vision}
                onChange={(e) => handleInputChange('legacy_vision', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="What do you want to be remembered for? What impact do you want to leave?"
              />
            </div>
          </div>
        </div>

        {/* Section C: S.T.E.P. Reflection Model */}
        <div id="section-c" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-40">
          <SectionHeader
            icon={Activity}
            letter="C"
            title="S.T.E.P. Reflection Model"
            description="Think about what inspires and drains you across these categories: Spiritual, Things, Experiences, & People"
            helpContent={
              <div className="space-y-3">
                <p>The S.T.E.P. model helps you identify what energizes and depletes you across four key dimensions of life.</p>
                <p><strong>Spiritual:</strong> Explore your core beliefs, passions, and what centers you.</p>
                <p><strong>Things:</strong> Reflect on material possessions - what you have, want, and don't need.</p>
                <p><strong>Experiences:</strong> Consider what you want to pursue or avoid in the coming years.</p>
                <p><strong>People:</strong> Identify your most important relationships and types of people to avoid.</p>
                <p><strong>Core Values:</strong> Reflect on people you admire and the traits they embody.</p>
              </div>
            }
          />

          <div className="space-y-8">
            {/* Spiritual Section */}
            <div className="space-y-4">
              <h4 className="text-md font-bold text-gray-900 border-b-2 border-purple-200 pb-2">
                Spiritual – What drives you spiritually? Where does your passion come from?
              </h4>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  I believe in... why?
                </label>
                <textarea
                  value={formData.step_spiritual_believe}
                  onChange={(e) => handleInputChange('step_spiritual_believe', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                  placeholder="What are your core spiritual beliefs and why do they matter to you?"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  I love to... why?
                </label>
                <textarea
                  value={formData.step_spiritual_love}
                  onChange={(e) => handleInputChange('step_spiritual_love', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                  placeholder="What spiritual activities or practices do you love and why?"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  More than anything else, I believe my center is...
                </label>
                <textarea
                  value={formData.step_spiritual_center}
                  onChange={(e) => handleInputChange('step_spiritual_center', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                  placeholder="What is at the core of your spiritual life?"
                />
              </div>
            </div>

            {/* Things Section */}
            <div className="space-y-4">
              <h4 className="text-md font-bold text-gray-900 border-b-2 border-purple-200 pb-2">
                Things – What matters most to you materially?
              </h4>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  I already have...
                </label>
                <textarea
                  value={formData.step_things_have}
                  onChange={(e) => handleInputChange('step_things_have', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                  placeholder="What material things do you already have that matter to you?"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  I would like more of...
                </label>
                <textarea
                  value={formData.step_things_want}
                  onChange={(e) => handleInputChange('step_things_want', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                  placeholder="What material things would you like to acquire?"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  I don't need...
                </label>
                <textarea
                  value={formData.step_things_dont_need}
                  onChange={(e) => handleInputChange('step_things_dont_need', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                  placeholder="What material things are you ready to let go of or don't need?"
                />
              </div>
            </div>

            {/* Experiences Section */}
            <div className="space-y-4">
              <h4 className="text-md font-bold text-gray-900 border-b-2 border-purple-200 pb-2">
                Experiences – What do you want to experience (or avoid) in the next 10 years?
              </h4>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  If I had more time I would...
                </label>
                <textarea
                  value={formData.step_experiences_more_time}
                  onChange={(e) => handleInputChange('step_experiences_more_time', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                  placeholder="What experiences would you pursue if you had more time?"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  If I could, I would avoid...
                </label>
                <textarea
                  value={formData.step_experiences_avoid}
                  onChange={(e) => handleInputChange('step_experiences_avoid', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                  placeholder="What experiences or activities would you prefer to avoid?"
                />
              </div>
            </div>

            {/* People Section */}
            <div className="space-y-4">
              <h4 className="text-md font-bold text-gray-900 border-b-2 border-purple-200 pb-2">
                People – Who are your most important relationships?
              </h4>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  The people that are most important to me are...
                </label>
                <textarea
                  value={formData.step_people_important}
                  onChange={(e) => handleInputChange('step_people_important', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                  placeholder="List the people who matter most in your life"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  The types of people I would like to avoid are...
                </label>
                <textarea
                  value={formData.step_people_avoid}
                  onChange={(e) => handleInputChange('step_people_avoid', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                  placeholder="What types of people drain your energy or you'd prefer to avoid?"
                />
              </div>
            </div>

            {/* Core Values Section */}
            <div className="space-y-4">
              <h4 className="text-md font-bold text-gray-900 border-b-2 border-purple-200 pb-2">
                Core Values – Reflect on people you admire. What traits do they embody?
              </h4>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  The people I look up to are...
                </label>
                <textarea
                  value={formData.step_core_values_admire}
                  onChange={(e) => handleInputChange('step_core_values_admire', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                  placeholder="Who are the people you admire and look up to?"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  The traits that they embody are...
                </label>
                <textarea
                  value={formData.step_core_values_traits}
                  onChange={(e) => handleInputChange('step_core_values_traits', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                  placeholder="What specific traits and characteristics do these people embody?"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Section D: Personal Wealth Gap */}
        <div id="section-d" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-40">
          <SectionHeader
            icon={DollarSign}
            letter="D"
            title="Personal Wealth Gap"
            description="Calculate what you need for financial independence"
            helpContent={
              <div className="space-y-3">
                <p>This section displays your personal wealth gap analysis, automatically populated from the Wealth Gap Calculator.</p>
                <p><strong>Exit Year & Age:</strong> Shows your planned exit year and how old you'll be at that time.</p>
                <p><strong>Net Monthly Needs:</strong> Your monthly cash requirement after accounting for outside income sources, shown for both current state and projected at exit (adjusted for inflation and lifestyle changes).</p>
                <p><strong>Total Capital Needed:</strong> The total amount of capital required at exit to maintain your lifestyle indefinitely using a safe withdrawal rate (typically 3-4%).</p>
                <p><strong>Net Worth:</strong> Your current net worth (excluding business) and projected net worth at exit after accounting for savings and investment growth.</p>
                <p><strong>Est. Business Exit Value:</strong> Your current estimated business value and projected value at exit.</p>
                <p><strong>Wealth Surplus/Gap:</strong> Shows whether you're on track (surplus) or how much you need from the business sale to reach your financial independence goal.</p>
                <p className="text-sm text-gray-600 italic mt-3">To update these values, visit the Wealth Gap Calculator in the Valuation section.</p>
              </div>
            }
          />

          {/* Auto-populated Wealth Gap Data */}
          {wealthGap ? (
            <div className="space-y-6">
              <div className="bg-orange-50 rounded-lg p-6 border-2 border-orange-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Personal Wealth Gap Analysis</h3>
                  <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded border border-gray-200">Auto-populated from Wealth Gap Calculator</span>
                </div>

                {/* Exit Year and Age */}
                {wealthGap.exit_year && (
                  <div className="mb-4 bg-purple-50 rounded-lg p-3 border border-purple-200">
                    <p className="text-base text-gray-700 text-center">
                      Exit Year: <span className="font-bold text-purple-800">{wealthGap.exit_year}</span>
                      {wealthGap.current_age && <> @ Age <span className="font-bold text-purple-800">{wealthGap.current_age + Math.max(0, wealthGap.exit_year - new Date().getFullYear())}</span></>}
                    </p>
                  </div>
                )}

                {/* Table Layout */}
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="grid grid-cols-12 gap-2 text-sm">
                    {/* Header Row */}
                    <div className="col-span-6 font-bold text-gray-900"></div>
                    <div className="col-span-3 font-bold text-gray-900 text-center">Current</div>
                    <div className="col-span-3 font-bold text-gray-900 text-center">At Exit</div>

                    {/* Net Monthly Needs */}
                    {wealthGap.wealth_goal_method === 'monthly_needs' && (
                      <>
                        <div className="col-span-12 border-t border-gray-300 mt-1 pt-2"></div>
                        <div className="col-span-6 text-gray-700 font-semibold">Net Monthly Needs</div>
                        <div className="col-span-3 text-center font-semibold text-blue-700">
                          {(() => {
                            const totalCurrentMonthlyNeed = (wealthGap.monthly_cash_need || 0) + (wealthGap.non_business_monthly_cash_needs || 0);
                            return formatCurrency(Math.max(0, totalCurrentMonthlyNeed - (wealthGap.outside_income_monthly || 0)));
                          })()}
                        </div>
                        <div className="col-span-3 text-center font-semibold text-blue-700">
                          {(() => {
                            const currentYear = new Date().getFullYear();
                            const yearsUntilExit = Math.max(0, (wealthGap.exit_year || currentYear) - currentYear);
                            const totalCurrentMonthlyNeed = (wealthGap.monthly_cash_need || 0) + (wealthGap.non_business_monthly_cash_needs || 0);
                            const inflationMultiplier = Math.pow(1 + (wealthGap.annual_inflation_rate || 2) / 100, yearsUntilExit);
                            const monthlyNeedAtExit = totalCurrentMonthlyNeed * inflationMultiplier * (1 + (wealthGap.lifestyle_change_percentage || 0) / 100);
                            const outsideIncomeGrowthRate = (wealthGap.outside_income_annual_increase || 0) / 100;
                            const yearsUntilRetirement = Math.max(0, (wealthGap.retirement_age || wealthGap.current_age || 0) - (wealthGap.current_age || 0));
                            const futureOutsideIncomeMonthly = (wealthGap.outside_income_monthly || 0) * Math.pow(1 + outsideIncomeGrowthRate, yearsUntilRetirement);
                            const futureNetMonthlyNeed = Math.max(0, monthlyNeedAtExit - futureOutsideIncomeMonthly);
                            return formatCurrency(futureNetMonthlyNeed);
                          })()}
                        </div>
                      </>
                    )}

                    {/* Total Capital Needed */}
                    <div className="col-span-12 border-t border-gray-300 mt-1 pt-2"></div>
                    <div className="col-span-6 text-gray-700 font-semibold">Total Capital Needed</div>
                    <div className="col-span-3 text-center text-gray-500">—</div>
                    <div className="col-span-3 text-center font-semibold text-indigo-700">
                      {wealthGap.required_sale_price_data?.total_capital_needed !== undefined
                        ? formatCurrency(wealthGap.required_sale_price_data.total_capital_needed)
                        : formatCurrency(wealthGap.calculated_wealth_goal)}
                    </div>

                    {/* Net Worth */}
                    <div className="col-span-12 border-t border-gray-300 mt-1 pt-2"></div>
                    <div className="col-span-6 text-gray-700 font-semibold">Net Worth</div>
                    <div className="col-span-3 text-center font-semibold text-emerald-700">
                      {formatCurrency(wealthGap.current_net_worth)}
                    </div>
                    <div className="col-span-3 text-center font-semibold text-emerald-700">
                      {wealthGap.required_sale_price_data?.future_net_worth !== undefined
                        ? formatCurrency(wealthGap.required_sale_price_data.future_net_worth)
                        : '—'}
                    </div>

                    {/* Estimated Business Exit Value */}
                    <div className="col-span-12 border-t border-gray-300 mt-1 pt-2"></div>
                    <div className="col-span-6 text-gray-700 font-semibold">Est. Business Exit Value</div>
                    <div className="col-span-3 text-center font-semibold text-cyan-700">
                      {formatCurrency(wealthGap.desired_sale_price || 0)}
                    </div>
                    <div className="col-span-3 text-center font-semibold text-cyan-700">
                      {formatCurrency(wealthGap.future_business_value || wealthGap.desired_sale_price || 0)}
                    </div>
                  </div>
                </div>

                {/* Wealth Surplus or Gap Summary */}
                {wealthGap.wealth_goal_method === 'monthly_needs' && wealthGap.required_sale_price_data?.total_capital_needed !== undefined && (
                  <div className={`mt-4 rounded-lg p-3 border-2 ${
                    wealthGap.required_sale_price_data.wealth_surplus > 0
                      ? 'bg-green-100 border-green-300'
                      : 'bg-orange-100 border-orange-300'
                  }`}>
                    <p className="text-base text-gray-800 text-center">
                      {wealthGap.required_sale_price_data.wealth_surplus > 0 ? (
                        <>
                          <span className="font-bold text-green-800">Wealth Surplus:</span>{' '}
                          <span className="font-bold text-green-900">{formatCurrency(wealthGap.required_sale_price_data.wealth_surplus)}</span>
                          <span className="text-green-700"> (You're on track!)</span>
                        </>
                      ) : (
                        <>
                          <span className="font-bold text-orange-800">Required from Business Sale:</span>{' '}
                          <span className="font-bold text-orange-900">{formatCurrency(Math.abs(wealthGap.required_sale_price_data.wealth_surplus))}</span>
                        </>
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-6 text-center border border-gray-200">
              <p className="text-gray-600 text-sm">
                No wealth gap data available. Please complete the <strong>Wealth Gap Calculator</strong> to see your analysis here.
              </p>
            </div>
          )}
        </div>

        {/* Section E: Financial Planning Details */}
        <div id="section-e" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-40">
          <SectionHeader
            icon={DollarSign}
            letter="E"
            title="Financial Planning Details"
            description="Your investment approach, estate planning, and wealth management strategy"
            helpContent={
              <div className="space-y-3">
                <p>This section captures important details about your financial planning and wealth management.</p>
                <p>Understanding your risk tolerance, estate planning status, and wealth strategies helps ensure alignment with your exit goals.</p>
              </div>
            }
          />

          <div className="space-y-6">

            {/* Investment Risk Tolerance - Radio Buttons */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Investment Risk Tolerance
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { value: 'conservative', label: 'Conservative', desc: 'Preserve capital, minimal risk' },
                  { value: 'moderate', label: 'Moderate', desc: 'Balanced growth and safety' },
                  { value: 'aggressive', label: 'Aggressive', desc: 'Higher growth potential' },
                  { value: 'very_aggressive', label: 'Very Aggressive', desc: 'Maximum growth focus' }
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`
                      flex flex-col p-3 border-2 rounded-lg cursor-pointer transition-all
                      ${formData.investment_risk_tolerance === option.value
                        ? 'border-purple-600 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300 bg-white'
                      }
                    `}
                  >
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="investment_risk_tolerance"
                        value={option.value}
                        checked={formData.investment_risk_tolerance === option.value}
                        onChange={(e) => handleInputChange('investment_risk_tolerance', e.target.value)}
                        className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-900">{option.label}</span>
                    </div>
                    <span className="ml-6 text-xs text-gray-600 mt-1">{option.desc}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Estate Planning Documents - Checkboxes */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Estate Planning Documents in Place
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { value: 'will', label: 'Last Will and Testament' },
                  { value: 'trust', label: 'Revocable Living Trust' },
                  { value: 'poa', label: 'Durable Power of Attorney' },
                  { value: 'healthcare_directive', label: 'Healthcare Directive / Living Will' },
                  { value: 'beneficiary_designations', label: 'Updated Beneficiary Designations' },
                  { value: 'guardianship', label: 'Guardianship Designations (if applicable)' }
                ].map((doc) => (
                  <label
                    key={doc.value}
                    className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.estate_planning_documents.includes(doc.value)}
                      onChange={(e) => {
                        const currentDocs = formData.estate_planning_documents || [];
                        const newDocs = e.target.checked
                          ? [...currentDocs, doc.value]
                          : currentDocs.filter(d => d !== doc.value);
                        handleInputChange('estate_planning_documents', newDocs);
                      }}
                      className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <span className="ml-3 text-sm text-gray-900">{doc.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Primary Wealth Accumulation Strategy - Dropdown */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Primary Wealth Accumulation Strategy
              </label>
              <select
                value={formData.primary_wealth_strategy}
                onChange={(e) => handleInputChange('primary_wealth_strategy', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
              >
                <option value="">Select primary strategy...</option>
                <option value="business_sale">Business Sale Proceeds</option>
                <option value="investments">Investment Portfolio Growth</option>
                <option value="real_estate">Real Estate Investments</option>
                <option value="passive_income">Passive Income Streams</option>
                <option value="inheritance">Expected Inheritance</option>
                <option value="continued_earnings">Continued Earnings Post-Exit</option>
                <option value="combination">Combination of Multiple Strategies</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Financial Planning Status - Radio Buttons */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Financial Planning Status
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { value: 'no_plan', label: 'No Formal Plan', desc: 'Haven\'t started formal financial planning' },
                  { value: 'diy', label: 'DIY Planning', desc: 'Managing my own financial plan' },
                  { value: 'working_with_advisor', label: 'Working with Advisor', desc: 'Have a financial advisor but plan is in progress' },
                  { value: 'comprehensive', label: 'Comprehensive Plan', desc: 'Have a complete, reviewed financial plan' }
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`
                      flex flex-col p-3 border-2 rounded-lg cursor-pointer transition-all
                      ${formData.financial_planning_status === option.value
                        ? 'border-purple-600 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300 bg-white'
                      }
                    `}
                  >
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="financial_planning_status"
                        value={option.value}
                        checked={formData.financial_planning_status === option.value}
                        onChange={(e) => handleInputChange('financial_planning_status', e.target.value)}
                        className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-900">{option.label}</span>
                    </div>
                    <span className="ml-6 text-xs text-gray-600 mt-1">{option.desc}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Major Financial Concerns - Textarea */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Major Financial Concerns or Questions
              </label>
              <textarea
                value={formData.major_financial_concerns}
                onChange={(e) => handleInputChange('major_financial_concerns', e.target.value)}
                rows={4}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="What are your biggest financial concerns or unanswered questions? (e.g., Will I have enough? How do I minimize taxes? What if the market crashes? Healthcare costs? Inflation impact?)"
              />
            </div>

            {/* Source of Wealth - Checkboxes */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Sources of Wealth (Select all that apply)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { value: 'business_ownership', label: 'Business Ownership' },
                  { value: 'employment_income', label: 'Employment/Salary Income' },
                  { value: 'investments', label: 'Investment Portfolio' },
                  { value: 'real_estate', label: 'Real Estate Holdings' },
                  { value: 'inheritance', label: 'Inheritance/Family Wealth' },
                  { value: 'entrepreneurship', label: 'Entrepreneurial Ventures' },
                  { value: 'professional_practice', label: 'Professional Practice (Law, Medical, etc.)' },
                  { value: 'royalties_ip', label: 'Royalties/Intellectual Property' },
                  { value: 'other', label: 'Other' }
                ].map((source) => (
                  <label key={source.value} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.source_of_wealth.includes(source.value)}
                      onChange={(e) => {
                        const currentSources = formData.source_of_wealth || [];
                        const newSources = e.target.checked
                          ? [...currentSources, source.value]
                          : currentSources.filter(s => s !== source.value);
                        handleInputChange('source_of_wealth', newSources);
                      }}
                      className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <span className="ml-3 text-sm text-gray-900">{source.label}</span>
                  </label>
                ))}
              </div>

              {formData.source_of_wealth.includes('other') && (
                <input
                  type="text"
                  value={formData.source_of_wealth_other}
                  onChange={(e) => handleInputChange('source_of_wealth_other', e.target.value)}
                  placeholder="Please specify other source of wealth"
                  className="mt-3 w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                />
              )}
            </div>

            {/* Use of Wealth - Checkboxes */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Use of Wealth (Select all that apply)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { value: 'lifestyle_support', label: 'Lifestyle Support & Living Expenses' },
                  { value: 'family_security', label: 'Family Security & Education' },
                  { value: 'reinvestment', label: 'Reinvestment & Wealth Growth' },
                  { value: 'philanthropy', label: 'Philanthropy & Charitable Giving' },
                  { value: 'legacy_building', label: 'Legacy Building & Generational Wealth' },
                  { value: 'retirement_planning', label: 'Retirement Planning & Independence' },
                  { value: 'business_expansion', label: 'Business Expansion & Ventures' },
                  { value: 'experiences_travel', label: 'Experiences & Travel' },
                  { value: 'other', label: 'Other' }
                ].map((use) => (
                  <label key={use.value} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.use_of_wealth.includes(use.value)}
                      onChange={(e) => {
                        const currentUses = formData.use_of_wealth || [];
                        const newUses = e.target.checked
                          ? [...currentUses, use.value]
                          : currentUses.filter(u => u !== use.value);
                        handleInputChange('use_of_wealth', newUses);
                      }}
                      className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <span className="ml-3 text-sm text-gray-900">{use.label}</span>
                  </label>
                ))}
              </div>

              {formData.use_of_wealth.includes('other') && (
                <input
                  type="text"
                  value={formData.use_of_wealth_other}
                  onChange={(e) => handleInputChange('use_of_wealth_other', e.target.value)}
                  placeholder="Please specify other use of wealth"
                  className="mt-3 w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                />
              )}
            </div>
          </div>
        </div>

        {/* Section F: 10-Year Life Vision */}
        <div id="section-f" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-40">
          <SectionHeader
            icon={Target}
            letter="F"
            title="10-Year Life Vision"
            description="Paint a picture of your ideal life 10 years from now"
            helpContent={
              <div className="space-y-3">
                <p>This section helps you dream about your whole life, not just your business.</p>
                <p>Think 10 years out - what does your ideal life look like?</p>
                <p>Consider: Where do you live? How do you spend your time? What matters most?</p>
              </div>
            }
          />

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Where Are You Living?
              </label>
              <textarea
                value={formData.living_location}
                onChange={(e) => handleInputChange('living_location', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="Location, type of home, lifestyle (e.g., mountains, beach, city, travel)"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                How Are You Spending Your Time?
              </label>
              <textarea
                value={formData.time_spending}
                onChange={(e) => handleInputChange('time_spending', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="Daily activities, hobbies, work, family time, travel, etc."
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Your Faith & Spiritual Life
              </label>
              <textarea
                value={formData.faith_vision}
                onChange={(e) => handleInputChange('faith_vision', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="What does your walk with God look like?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Your Work & Contribution
              </label>
              <textarea
                value={formData.work_vision}
                onChange={(e) => handleInputChange('work_vision', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="Still working? Retired? Volunteering? Consulting?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Your Marriage & Family
              </label>
              <textarea
                value={formData.marriage_vision}
                onChange={(e) => handleInputChange('marriage_vision', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="What do your key relationships look like?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Your Generosity & Giving
              </label>
              <textarea
                value={formData.giving_vision}
                onChange={(e) => handleInputChange('giving_vision', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="How are you using your resources to make an impact?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Your Health & Vitality
              </label>
              <textarea
                value={formData.health_vision}
                onChange={(e) => handleInputChange('health_vision', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="What does physical, mental, and emotional health look like?"
              />
            </div>
          </div>
        </div>

        {/* Section G: 3-5 Year Personal Picture */}
        <div id="section-g" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-40">
          <SectionHeader
            icon={CheckSquare}
            letter="G"
            title="3-5 Year Personal Picture"
            description="What specific transformation goals do you have?"
            helpContent={
              <div className="space-y-3">
                <p>This section brings your 10-year vision into sharper focus for the next 3-5 years.</p>
                <p>What needs to change or transform in each area of your life?</p>
                <p>Be specific about targets and milestones.</p>
              </div>
            }
          />

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Marriage Transformation
              </label>
              <textarea
                value={formData.marriage_transformation}
                onChange={(e) => handleInputChange('marriage_transformation', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="What needs to improve or change in your marriage?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Kids & Family Transformation
              </label>
              <textarea
                value={formData.kids_transformation}
                onChange={(e) => handleInputChange('kids_transformation', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="What do you want for your kids and family relationships?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Health Transformation
              </label>
              <textarea
                value={formData.health_transformation}
                onChange={(e) => handleInputChange('health_transformation', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="Specific health goals (weight, fitness, energy, habits)"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Spiritual Life Transformation
              </label>
              <textarea
                value={formData.spiritual_transformation}
                onChange={(e) => handleInputChange('spiritual_transformation', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="How do you want to grow spiritually?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Work/Business Role Transformation
              </label>
              <textarea
                value={formData.work_transformation}
                onChange={(e) => handleInputChange('work_transformation', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="How does your role in the business need to evolve?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Net Worth Transformation
              </label>
              <textarea
                value={formData.net_worth_transformation}
                onChange={(e) => handleInputChange('net_worth_transformation', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="Financial targets and wealth building goals"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                What Needs to Change to Make This Happen?
              </label>
              <textarea
                value={formData.changes_needed}
                onChange={(e) => handleInputChange('changes_needed', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="What habits, commitments, or circumstances need to change?"
              />
            </div>
          </div>
        </div>

        {/* Section H: Perfect Week Exercise */}
        <div id="section-h" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-40">
          <SectionHeader
            icon={Calendar}
            letter="H"
            title="Perfect Week Exercise"
            description="Design your ideal weekly rhythm"
            helpContent={
              <div className="space-y-3">
                <p>This exercise helps you design how you want to spend your time.</p>

                <p className="font-semibold text-gray-900 pt-2">Time Management & Rhythm Score (Auto-populated):</p>
                <p><strong>Time Management & Rhythm Score:</strong> Automatically populated from your Personal Gap Assessment. This score measures how effectively you manage time and maintain healthy life rhythms.</p>

                <p className="font-semibold text-gray-900 pt-2">Perfect Week Exercise:</p>
                <p><strong>Perfect Week:</strong> If you could design your ideal week (3-5 years from now), what would it look like?</p>
                <p><strong>Reality Check:</strong> Compare your perfect week to your current reality. What needs to change?</p>
              </div>
            }
          />

          <div className="space-y-6">
            {/* Time Management & Rhythm Score - Auto-populated */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">Time Management & Rhythm Assessment</h3>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Auto-populated from Personal Gap Assessment</span>
              </div>

              {personalAssessment?.category_scores?.time_management !== null &&
               personalAssessment?.category_scores?.time_management !== undefined ? (
                <div className="bg-teal-50 rounded-lg p-6 border-2 border-teal-200">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className={`text-center p-4 rounded-lg border ${getScoreBgColor(personalAssessment.category_scores.time_management)}`}>
                        <p className={`text-3xl font-bold ${getScoreColor(personalAssessment.category_scores.time_management)}`}>
                          {formatPercentage(personalAssessment.category_scores.time_management)}
                        </p>
                        <p className="text-xs text-gray-600 mt-1 font-semibold">Score</p>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Time Management & Rhythm</h4>
                      <p className="text-sm text-gray-700">
                        {PERSONAL_CATEGORY_DESCRIPTIONS['Time Management & Rhythm']?.summary}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center">
                  <p className="text-sm text-gray-600">Complete assessment to see score</p>
                </div>
              )}
            </div>

            <div className="bg-purple-50 rounded-lg p-6 space-y-4">
              <h3 className="font-semibold text-gray-900">Your Perfect Week (3-5 years from now)</h3>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Mornings (6am-12pm)
                </label>
                <textarea
                  value={formData.perfect_week_mornings}
                  onChange={(e) => handleInputChange('perfect_week_mornings', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition bg-white"
                  placeholder="How do you spend your mornings?"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Afternoons (12pm-6pm)
                </label>
                <textarea
                  value={formData.perfect_week_afternoons}
                  onChange={(e) => handleInputChange('perfect_week_afternoons', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition bg-white"
                  placeholder="How do you spend your afternoons?"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Evenings (6pm-10pm)
                </label>
                <textarea
                  value={formData.perfect_week_evenings}
                  onChange={(e) => handleInputChange('perfect_week_evenings', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition bg-white"
                  placeholder="How do you spend your evenings?"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Weekends
                </label>
                <textarea
                  value={formData.perfect_week_weekends}
                  onChange={(e) => handleInputChange('perfect_week_weekends', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition bg-white"
                  placeholder="How do you spend your weekends?"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Current Week Reality
              </label>
              <textarea
                value={formData.current_week_reality}
                onChange={(e) => handleInputChange('current_week_reality', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="Describe your current weekly rhythm - how do you actually spend your time?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Gap Analysis: What Needs to Change?
              </label>
              <textarea
                value={formData.gap_analysis}
                onChange={(e) => handleInputChange('gap_analysis', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="What's the gap between your perfect week and current reality? What needs to change?"
              />
            </div>
          </div>
        </div>

        {/* Section I: Relationships & Family Alignment */}
        <div id="section-i" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-40">
          <SectionHeader
            icon={Users}
            letter="I"
            title="Relationships & Family Alignment"
            description="Get on the same page with those who matter most"
            helpContent={
              <div className="space-y-3">
                <p>Exit planning is not just a business decision - it's a family decision.</p>
                <p>This section helps you think through key relationships and alignment.</p>
                <p><strong>Relational Health & Marriage Score:</strong> Automatically populated from your Personal Gap Assessment, reflecting the health and strength of your marriage relationship.</p>
                <p><strong>Family Relationships & Legacy Score:</strong> Automatically populated from your Personal Gap Assessment, measuring the health of family relationships and intentionality about family legacy.</p>
                <p><strong>Key question:</strong> Is your spouse on board with your exit plan?</p>
              </div>
            }
          />

          <div className="space-y-6">
            {/* Relationship Assessment Scores - Auto-populated */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">Relationship & Family Assessment</h3>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Auto-populated from Personal Gap Assessment</span>
              </div>

              {personalAssessment?.category_scores?.relational_health !== null &&
               personalAssessment?.category_scores?.relational_health !== undefined &&
               personalAssessment?.category_scores?.family_relationships !== null &&
               personalAssessment?.category_scores?.family_relationships !== undefined ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Relational Health & Marriage */}
                  <div className="bg-blue-50 rounded-lg p-6 border-2 border-blue-200">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className={`text-center p-4 rounded-lg border ${getScoreBgColor(personalAssessment.category_scores.relational_health)}`}>
                          <p className={`text-3xl font-bold ${getScoreColor(personalAssessment.category_scores.relational_health)}`}>
                            {formatPercentage(personalAssessment.category_scores.relational_health)}
                          </p>
                          <p className="text-xs text-gray-600 mt-1 font-semibold">Score</p>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Relational Health & Marriage</h4>
                        <p className="text-sm text-gray-700">
                          {PERSONAL_CATEGORY_DESCRIPTIONS['Relational Health & Marriage']?.summary ||
                           'Measures the health and strength of your marriage relationship.'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Family Relationships & Legacy */}
                  <div className="bg-purple-50 rounded-lg p-6 border-2 border-purple-200">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className={`text-center p-4 rounded-lg border ${getScoreBgColor(personalAssessment.category_scores.family_relationships)}`}>
                          <p className={`text-3xl font-bold ${getScoreColor(personalAssessment.category_scores.family_relationships)}`}>
                            {formatPercentage(personalAssessment.category_scores.family_relationships)}
                          </p>
                          <p className="text-xs text-gray-600 mt-1 font-semibold">Score</p>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Family Relationships & Legacy</h4>
                        <p className="text-sm text-gray-700">
                          {PERSONAL_CATEGORY_DESCRIPTIONS['Family Relationships & Legacy']?.summary ||
                           'Evaluates the health and depth of your family relationships and your intentionality about family legacy.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center">
                  <p className="text-sm text-gray-600">Complete assessment to see scores</p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Relationship Priorities (Rank your top 5)
              </label>
              <textarea
                value={formData.relationship_priorities}
                onChange={(e) => handleInputChange('relationship_priorities', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="1. Spouse, 2. Kids, 3. Parents, 4. Business partners, etc."
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Key Conversations You Need to Have
              </label>
              <textarea
                value={formData.key_conversations}
                onChange={(e) => handleInputChange('key_conversations', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="Who do you need to talk to? What conversations have you been avoiding?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Spouse Alignment Check
              </label>
              <textarea
                value={formData.spouse_alignment}
                onChange={(e) => handleInputChange('spouse_alignment', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="Is your spouse aligned with your exit plans? What questions or concerns do they have?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Family Concerns or Conflicts
              </label>
              <textarea
                value={formData.family_concerns}
                onChange={(e) => handleInputChange('family_concerns', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="What family issues need to be addressed?"
              />
            </div>
          </div>
        </div>

        {/* Section J: Health, Hobbies & Personal Growth */}
        <div id="section-j" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-40">
          <SectionHeader
            icon={Activity}
            letter="J"
            title="Health, Hobbies & Personal Growth"
            description="Assess your wellbeing and plan for meaningful activities beyond work"
            helpContent={
              <div className="space-y-3">
                <p>You can't build a great business or enjoy your exit if you're burned out or unhealthy.</p>
                <p>This section helps you assess your current health and energy levels, and plan for meaningful personal growth beyond work.</p>

                <p className="font-semibold text-gray-900 pt-2">Health & Personal Growth Scores (Auto-populated):</p>
                <p><strong>Physical Health & Vitality Score:</strong> Automatically populated from your Personal Gap Assessment. This score reflects your current physical health status based on your assessment responses.</p>
                <p><strong>Hobbies, Interests & Personal Growth Score:</strong> Automatically populated from your Personal Gap Assessment, evaluating your engagement with meaningful activities and personal development pursuits beyond work.</p>

                <p className="font-semibold text-gray-900 pt-2">Health Planning:</p>
                <p><strong>Current Health Constraints:</strong> Identify any physical, mental, or emotional health issues that are currently limiting you or affecting your quality of life.</p>
                <p><strong>3-5 Year Health Vision:</strong> Define what optimal health looks like for you. What energy level do you want to maintain? What activities do you want to be able to do?</p>
                <p><strong>Health Action Steps:</strong> Outline specific actions you'll take to achieve your health vision (exercise routines, diet changes, sleep improvements, stress management practices, etc.).</p>

                <p className="font-semibold text-gray-900 pt-2">Hobbies & Personal Growth Planning:</p>
                <p><strong>Current Hobbies & Interests:</strong> Document the hobbies and interests you currently enjoy and how much time you dedicate to them.</p>
                <p><strong>Hobbies You Want to Develop:</strong> Identify new hobbies, interests, or skills you'd like to explore post-exit. What have you always wanted to learn or try?</p>
                <p><strong>Time Allocation for Hobbies Post-Exit:</strong> Plan how much time you'll dedicate to hobbies and personal interests after your business exit.</p>
                <p><strong>Communities or Groups:</strong> Identify clubs, groups, mentors, or communities you'd like to join related to your interests. Social connection around hobbies enhances fulfillment.</p>

                <p className="pt-2"><strong>Key question:</strong> Do you have the physical and mental capacity to execute your plan and enjoy life beyond work?</p>
              </div>
            }
          />

          <div className="space-y-6">
            {/* Health & Personal Growth Scores - Auto-populated */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">Health & Personal Growth Assessment</h3>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Auto-populated from Personal Gap Assessment</span>
              </div>

              {personalAssessment?.category_scores?.physical_health !== null &&
               personalAssessment?.category_scores?.physical_health !== undefined &&
               personalAssessment?.category_scores?.hobbies_interests !== null &&
               personalAssessment?.category_scores?.hobbies_interests !== undefined ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Physical Health & Vitality */}
                  <div className="bg-green-50 rounded-lg p-6 border-2 border-green-200">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className={`text-center p-4 rounded-lg border ${getScoreBgColor(personalAssessment.category_scores.physical_health)}`}>
                          <p className={`text-3xl font-bold ${getScoreColor(personalAssessment.category_scores.physical_health)}`}>
                            {formatPercentage(personalAssessment.category_scores.physical_health)}
                          </p>
                          <p className="text-xs text-gray-600 mt-1 font-semibold">Score</p>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Physical Health & Vitality</h4>
                        <p className="text-sm text-gray-700">
                          {PERSONAL_CATEGORY_DESCRIPTIONS['Physical Health & Vitality']?.summary ||
                           'Measures your current physical health status and commitment to wellness practices.'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Hobbies, Interests & Personal Growth */}
                  <div className="bg-cyan-50 rounded-lg p-6 border-2 border-cyan-200">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className={`text-center p-4 rounded-lg border ${getScoreBgColor(personalAssessment.category_scores.hobbies_interests)}`}>
                          <p className={`text-3xl font-bold ${getScoreColor(personalAssessment.category_scores.hobbies_interests)}`}>
                            {formatPercentage(personalAssessment.category_scores.hobbies_interests)}
                          </p>
                          <p className="text-xs text-gray-600 mt-1 font-semibold">Score</p>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Hobbies, Interests & Personal Growth</h4>
                        <p className="text-sm text-gray-700">
                          {PERSONAL_CATEGORY_DESCRIPTIONS['Hobbies, Interests & Personal Growth']?.summary ||
                           'Evaluates your engagement with meaningful activities and personal development pursuits beyond work.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center">
                  <p className="text-sm text-gray-600">Complete assessment to see scores</p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Current Health Constraints
              </label>
              <textarea
                value={formData.health_constraints}
                onChange={(e) => handleInputChange('health_constraints', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="What health issues are limiting you? (physical, mental, emotional)"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                3-5 Year Health Vision
              </label>
              <textarea
                value={formData.three_year_health_vision}
                onChange={(e) => handleInputChange('three_year_health_vision', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="What does optimal health look like for you? What energy level do you want?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Health Action Steps
              </label>
              <textarea
                value={formData.health_action_steps}
                onChange={(e) => handleInputChange('health_action_steps', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="What specific actions will you take? (exercise, diet, sleep, stress management, etc.)"
              />
            </div>

            {/* Hobbies & Personal Growth Fields */}
            <div className="pt-6 border-t border-gray-200">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Hobbies & Personal Growth Planning</h4>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Current Hobbies & Interests
                  </label>
                  <textarea
                    value={formData.current_hobbies}
                    onChange={(e) => handleInputChange('current_hobbies', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                    placeholder="What hobbies and interests do you currently enjoy? How much time do you spend on them?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Hobbies You Want to Develop
                  </label>
                  <textarea
                    value={formData.hobbies_to_develop}
                    onChange={(e) => handleInputChange('hobbies_to_develop', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                    placeholder="What new hobbies or interests would you like to explore? What skills do you want to learn?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Time Allocation for Hobbies Post-Exit
                  </label>
                  <textarea
                    value={formData.time_allocation_hobbies}
                    onChange={(e) => handleInputChange('time_allocation_hobbies', e.target.value)}
                    rows={2}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                    placeholder="How much time do you envision dedicating to hobbies after your business exit? (hours per week, days per month, etc.)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Communities or Groups Related to Your Interests
                  </label>
                  <textarea
                    value={formData.hobby_communities}
                    onChange={(e) => handleInputChange('hobby_communities', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                    placeholder="What clubs, groups, or communities would you like to join? Are there mentors or peers you'd like to connect with?"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section K: Work, Purpose & Contribution Post-Exit */}
        <div id="section-k" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-40">
          <SectionHeader
            icon={Briefcase}
            letter="K"
            title="Work, Purpose & Contribution Post-Exit"
            description="What will you do after you exit?"
            helpContent={
              <div className="space-y-3">
                <p>Many business owners struggle with identity and purpose after they exit their business.</p>
                <p><strong>Statistics show:</strong> Many who "retire to do nothing" struggle with depression and health issues.</p>
                <p><strong>Post-Exit Vision & Planning Score:</strong> Automatically populated from your Personal Gap Assessment, evaluating the clarity and specificity of your vision for life after business exit.</p>
                <p><strong>Life Transitions & Change Readiness Score:</strong> Automatically populated from your Personal Gap Assessment, measuring your emotional and psychological preparedness for major life transitions.</p>
                <p>This section helps you think through what meaningful work and contribution looks like post-exit.</p>
              </div>
            }
          />

          <div className="space-y-6">
            {/* Post-Exit Readiness Scores - Auto-populated */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">Post-Exit Readiness Assessment</h3>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Auto-populated from Personal Gap Assessment</span>
              </div>

              {personalAssessment?.category_scores?.exit_vision !== null &&
               personalAssessment?.category_scores?.exit_vision !== undefined &&
               personalAssessment?.category_scores?.life_transition !== null &&
               personalAssessment?.category_scores?.life_transition !== undefined ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Post-Exit Vision & Planning */}
                  <div className="bg-indigo-50 rounded-lg p-6 border-2 border-indigo-200">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className={`text-center p-4 rounded-lg border ${getScoreBgColor(personalAssessment.category_scores.exit_vision)}`}>
                          <p className={`text-3xl font-bold ${getScoreColor(personalAssessment.category_scores.exit_vision)}`}>
                            {formatPercentage(personalAssessment.category_scores.exit_vision)}
                          </p>
                          <p className="text-xs text-gray-600 mt-1 font-semibold">Score</p>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Post-Exit Vision & Planning</h4>
                        <p className="text-sm text-gray-700">
                          {PERSONAL_CATEGORY_DESCRIPTIONS['Post-Exit Vision & Planning']?.summary ||
                           'Evaluates the clarity and specificity of your vision for life after business exit.'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Life Transitions & Change Readiness */}
                  <div className="bg-amber-50 rounded-lg p-6 border-2 border-amber-200">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className={`text-center p-4 rounded-lg border ${getScoreBgColor(personalAssessment.category_scores.life_transition)}`}>
                          <p className={`text-3xl font-bold ${getScoreColor(personalAssessment.category_scores.life_transition)}`}>
                            {formatPercentage(personalAssessment.category_scores.life_transition)}
                          </p>
                          <p className="text-xs text-gray-600 mt-1 font-semibold">Score</p>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Life Transitions & Change Readiness</h4>
                        <p className="text-sm text-gray-700">
                          {PERSONAL_CATEGORY_DESCRIPTIONS['Life Transitions & Change Readiness']?.summary ||
                           'Measures your emotional and psychological preparedness for major life transitions, including business exit.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center">
                  <p className="text-sm text-gray-600">Complete assessment to see scores</p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Work Intentions Post-Exit
              </label>
              <textarea
                value={formData.work_intentions}
                onChange={(e) => handleInputChange('work_intentions', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="Will you work? Consult? Start something new? Fully retire?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Where Will You Contribute?
              </label>
              <textarea
                value={formData.contribution_focus}
                onChange={(e) => handleInputChange('contribution_focus', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="Nonprofit boards, mentoring, ministry, teaching, writing, etc."
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Avoiding the "Retire and Die" Trap
              </label>
              <textarea
                value={formData.avoiding_retire_die}
                onChange={(e) => handleInputChange('avoiding_retire_die', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="How will you stay engaged, purposeful, and healthy post-exit?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Meaningful Work Vision
              </label>
              <textarea
                value={formData.meaningful_work_vision}
                onChange={(e) => handleInputChange('meaningful_work_vision', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="What does meaningful work look like after you exit your business?"
              />
            </div>
          </div>
        </div>

        {/* Section L: Faith, Generosity & Eternal Impact */}
        <div id="section-l" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-40">
          <SectionHeader
            icon={Sparkles}
            letter="L"
            title="Faith, Generosity & Eternal Impact"
            description="How will you steward your resources for eternal impact?"
            helpContent={
              <div className="space-y-3">
                <p>This section helps you think through how you'll use your time, talent, and treasure for God's kingdom.</p>
                <p><strong>Key question:</strong> How do you want to be generous with what God has entrusted to you?</p>
                <p>Consider: Giving now vs. giving later, impact areas, legacy giving strategies.</p>
              </div>
            }
          />

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Spiritual Growth Goals
              </label>
              <textarea
                value={formData.spiritual_growth_goals}
                onChange={(e) => handleInputChange('spiritual_growth_goals', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="How do you want to grow in your faith? What spiritual disciplines do you want to develop?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Spiritual Practices & Rhythms
              </label>
              <textarea
                value={formData.spiritual_practices}
                onChange={(e) => handleInputChange('spiritual_practices', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="Prayer, Bible study, worship, fasting, solitude, etc."
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Giving Plan
              </label>
              <textarea
                value={formData.giving_plan}
                onChange={(e) => handleInputChange('giving_plan', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="What percentage do you want to give? To what causes? Give now vs. give later strategies?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Eternal Impact Vision
              </label>
              <textarea
                value={formData.eternal_impact_vision}
                onChange={(e) => handleInputChange('eternal_impact_vision', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="What kingdom impact do you want to make? What legacy of generosity do you want to leave?"
              />
            </div>
          </div>
        </div>

        {/* Section M: Preparing for Five Ds (Personal) */}
        <div id="section-m" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-40">
          <SectionHeader
            icon={Shield}
            letter="M"
            title="Preparing for the Five Ds (Personal)"
            description="Personal protection for Death, Disability, Divorce, Distress, Disagreement"
            helpContent={
              <div className="space-y-3">
                <p>The Five Ds apply personally as well as to your business:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong>Death:</strong> Personal estate planning, life insurance, legacy planning</li>
                  <li><strong>Disability:</strong> Disability insurance, health care directives, care plans</li>
                  <li><strong>Divorce:</strong> Marital stability, pre/post-nuptial planning</li>
                  <li><strong>Distress:</strong> Emergency fund, mental health resources, support systems</li>
                  <li><strong>Disagreement:</strong> Family communication, conflict resolution skills</li>
                </ul>
                <p>For each of the Five Ds, check off the preparedness items you have in place and add any additional notes about your current status and next steps.</p>
                <p>Identify your top priority actions to improve personal protection across all five areas.</p>
              </div>
            }
          />

          <div className="space-y-6">
            {/* Death */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Death (Personal estate plan, life insurance, legacy)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {['Personal will current and executed', 'Life insurance policy in place', 'Healthcare power of attorney signed', 'Living will or advance directive completed', 'Beneficiary designations reviewed', 'Funeral and burial wishes documented'].map((option) => (
                  <label key={option} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.five_ds_personal_death_items.includes(option)}
                      onChange={() => handleCheckboxChange('five_ds_personal_death_items', option)}
                      className="mr-3 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes
              </label>
              <textarea
                value={formData.five_ds_personal_death}
                onChange={(e) => handleInputChange('five_ds_personal_death', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="Rate preparedness (1-10) and describe current status"
              />
            </div>

            {/* Disability */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Disability (Personal disability insurance, care directives)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {['Personal disability insurance coverage', 'Long-term care insurance in place', 'Medical power of attorney designated', 'Care preferences documented', 'Support network identified', 'Emergency fund for extended disability'].map((option) => (
                  <label key={option} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.five_ds_personal_disability_items.includes(option)}
                      onChange={() => handleCheckboxChange('five_ds_personal_disability_items', option)}
                      className="mr-3 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes
              </label>
              <textarea
                value={formData.five_ds_personal_disability}
                onChange={(e) => handleInputChange('five_ds_personal_disability', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="Rate preparedness (1-10) and describe current status"
              />
            </div>

            {/* Divorce */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Divorce (Marital health, prenuptial/postnuptial considerations)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {['Marriage counseling or relationship support', 'Prenuptial or postnuptial agreement', 'Separate property designations clear', 'Individual financial accounts maintained', 'Estate planning considers marital changes', 'Regular marital health check-ins'].map((option) => (
                  <label key={option} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.five_ds_personal_divorce_items.includes(option)}
                      onChange={() => handleCheckboxChange('five_ds_personal_divorce_items', option)}
                      className="mr-3 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes
              </label>
              <textarea
                value={formData.five_ds_personal_divorce}
                onChange={(e) => handleInputChange('five_ds_personal_divorce', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="Rate preparedness (1-10) and describe current status"
              />
            </div>

            {/* Distress */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Distress (Emergency funds, mental health, support systems)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {['Personal emergency fund (6-12 months expenses)', 'Mental health professional relationship', 'Support network or accountability group', 'Stress management practices in place', 'Spiritual director or counselor', 'Crisis intervention plan documented'].map((option) => (
                  <label key={option} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.five_ds_personal_distress_items.includes(option)}
                      onChange={() => handleCheckboxChange('five_ds_personal_distress_items', option)}
                      className="mr-3 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes
              </label>
              <textarea
                value={formData.five_ds_personal_distress}
                onChange={(e) => handleInputChange('five_ds_personal_distress', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="Rate preparedness (1-10) and describe current status"
              />
            </div>

            {/* Disagreement */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Disagreement (Family conflict resolution, communication skills)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {['Regular family meetings or communication', 'Conflict resolution skills training', 'Family communication norms established', 'Mediation process identified', 'Healthy boundaries with extended family', 'Professional family counselor relationship'].map((option) => (
                  <label key={option} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.five_ds_personal_disagreement_items.includes(option)}
                      onChange={() => handleCheckboxChange('five_ds_personal_disagreement_items', option)}
                      className="mr-3 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes
              </label>
              <textarea
                value={formData.five_ds_personal_disagreement}
                onChange={(e) => handleInputChange('five_ds_personal_disagreement', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="Rate preparedness (1-10) and describe current status"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Priority Actions for Personal Five Ds
              </label>
              <textarea
                value={formData.five_ds_personal_priorities}
                onChange={(e) => handleInputChange('five_ds_personal_priorities', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="What are your top 3 priorities for personal protection?"
              />
            </div>
          </div>
        </div>

        {/* Section N: 90-Day Personal Focus */}
        <div id="section-n" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-40">
          <SectionHeader
            icon={Calendar}
            letter="N"
            title="90-Day Personal Focus"
            description="What personal priorities will you tackle in the next 90 days?"
            helpContent={
              <div className="space-y-3">
                <p>Just like your business, your personal life needs intentional focus and action.</p>
                <p><strong>Sprint Priorities:</strong> What 2-3 personal goals will you focus on?</p>
                <p><strong>Conversations:</strong> What important conversations do you need to have?</p>
                <p><strong>Quick Wins:</strong> What small actions can create immediate improvement?</p>
              </div>
            }
          />

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Personal Sprint Priorities (Next 90 Days)
              </label>
              <div className="space-y-3">
                {[0, 1, 2].map((index) => (
                  <input
                    key={index}
                    type="text"
                    value={formData.personal_sprint_priorities[index]}
                    onChange={(e) => handleArrayChange('personal_sprint_priorities', index, e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                    placeholder={`Priority ${index + 1} (e.g., improve marriage, get healthy, financial planning)`}
                  />
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Important Conversations to Have
              </label>
              <textarea
                value={formData.conversations_to_have}
                onChange={(e) => handleInputChange('conversations_to_have', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="Who do you need to talk to? About what?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Personal Quick Wins
              </label>
              <div className="space-y-3">
                {[0, 1, 2].map((index) => (
                  <input
                    key={index}
                    type="text"
                    value={formData.personal_quick_wins[index]}
                    onChange={(e) => handleArrayChange('personal_quick_wins', index, e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                    placeholder={`Quick Win ${index + 1} (small action, immediate benefit)`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Section O: Integration with Business Plan */}
        <div id="section-o" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-40">
          <SectionHeader
            icon={LinkIcon}
            letter="O"
            title="Integration with Business Plan"
            description="How do your personal and business plans align?"
            helpContent={
              <div className="space-y-3">
                <p>Your personal envisioning and business envisioning must work together.</p>
                <p><strong>Alignment Check:</strong> Does your business plan support your personal goals?</p>
                <p><strong>Conflicts:</strong> Where do your personal and business plans conflict?</p>
                <p>Example: You want more family time, but your business plan requires heavy travel for 2 years.</p>
              </div>
            }
          />

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Alignment Check
              </label>
              <textarea
                value={formData.alignment_check}
                onChange={(e) => handleInputChange('alignment_check', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="How well do your personal and business plans support each other?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Conflicts to Resolve
              </label>
              <textarea
                value={formData.conflicts_to_resolve}
                onChange={(e) => handleInputChange('conflicts_to_resolve', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                placeholder="Where do your personal goals conflict with your business plans? What needs to change?"
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-end gap-4 sticky bottom-0 bg-white border-t border-gray-200 pt-6 pb-2">
          {/* Save Status */}
          {saveStatus === 'success' && (
            <div className="flex items-center space-x-2 text-green-600">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-medium">Saved successfully!</span>
            </div>
          )}
          {saveStatus === 'error' && (
            <div className="flex items-center space-x-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">Error saving. Please try again.</span>
            </div>
          )}
          <button
            type="button"
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors shadow-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <Download className="w-5 h-5" />
            {downloading ? 'Generating PDF...' : 'Download PDF'}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-8 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors shadow-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Personal Envisioning'}
          </button>
        </div>
      </form>

      {/* Help Modal */}
      <HelpModal
        isOpen={helpModal.isOpen}
        onClose={closeHelp}
        title={helpModal.title}
      >
        {helpModal.content}
      </HelpModal>
    </>
  );
};

export default PersonalEnvisioning;
